import React, { useState, useCallback, useMemo } from "react";
import { withTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import styles from "./BusinessSettings.module.scss";
import { Redirect } from "react-router-dom";
import DealerMembershipStatusClient from "../../shared/clients/DealerMembershipStatusClient";
import HasPermissionTo from "../../common/HasPermissionTo";
import MultiToggleWidget from "../../shared/toggles/multiToggleWidget/MultiToggleWidget";
import ConfirmDialog from "../../shared/confirmDialog/ConfirmDialog";
import AppActionsLogClient from "../../shared/clients/AppActionsLogClient";
import { GET_DEALER_NAME } from "../../utils/EmpUtil";

function MembershipPanel(props) {
  const [isDealerActive, setIsDealerActive] = useState(props.dealer.status === "ACTIVE");
  const [openDialog, setOpenDialog] = useState(false);

  const deactivateDialogProps = useMemo(() => ({
    title: props.t("EmpActivation.confirmDealerDeactivationTitle"),
    bodyText: props.t("EmpActivation.confirmDealerDeactivationText"),
    confirm: props.t("EmpActivation.confirmDealerDeactivationButton"),
    cancel: props.t("EmpActivation.cancelDealerDeactivationButton"),
  }), [props.t]);

  const activateDialogProps = useMemo(() => ({
    title: props.t("EmpActivation.confirmDealerActivationTitle"),
    bodyText: props.t("EmpActivation.confirmDealerActivationText"),
    confirm: props.t("EmpActivation.confirmDealerActivationButton"),
    cancel: props.t("EmpActivation.cancelDealerActivationButton"),
  }), [props.t]);

  const handleEmpMembershipChange = useCallback((dealerId, event) => {
    const buttonStatus = event.target.checked;

    if (
      (isDealerActive && buttonStatus) ||
      (!isDealerActive && !buttonStatus)
    ) {
      return;
    }
    if (buttonStatus) {
      setOpenDialog(true);
    } else {
      setOpenDialog(true);
      return (
        <Redirect
          to={{
            pathname: "/dashboard",
          }}
        />
      );
    }
  }, [isDealerActive]);

  const handleClose = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleConfirmation = useCallback(() => {
    DealerMembershipStatusClient.toggleEmpMembership(
      props.user,
      props.dealer.completeDealerId,
      !isDealerActive
    )
      .then(() => {
        setIsDealerActive(prev => !prev);
        setOpenDialog(false);
      })
      .then(() => {
        const payload = {
          userId: props.user.userId,
          participationStatusEnabled: isDealerActive,
        };

        AppActionsLogClient.recordParticipationStatus(props.user, payload)
          .then(() => {})
          .catch(() => {});
      })
      .catch(() => {})
      .finally(() => {
        props.dealer.status = isDealerActive ? "ACTIVE" : "INACTIVE";
        props.updateDealerEmpMembership(props.dealer);
      });
  }, [props, isDealerActive]);

  const dealerId = props.dealer.dealerId;

  return (
    <Grid container className={styles.activationPanel}>
      <Grid item md={9}>
        <div className={`${styles.dealerName}`}>
          {`${dealerId}`} |{" "}
          {GET_DEALER_NAME(props.dealer, props.user.market)}
        </div>
        <ConfirmDialog
          open={openDialog}
          dialogProps={
            isDealerActive
              ? deactivateDialogProps
              : activateDialogProps
          }
          onConfirm={handleConfirmation}
          onCancel={handleClose}
          onClose={handleClose}
          boldTitle={false}
          {...props}
        />
      </Grid>
      <Grid item md={3}>
        <HasPermissionTo
          perform={["empMembership:write"]}
          permissions={props.user.permissions.rolePermissions}
          render={() => (
            <MultiToggleWidget
              {...props}
              isToggleDisabled={props.isMainViewingSDLR(
                props.dealer
              )}
              toggleName={`empMembership_${dealerId}`}
              settings={{
                acceptedText: props.t("EmpMembership.active"),
                rejectedText: props.t("EmpMembership.inactive"),
                suppClass: `${styles.toggleWrapperMain}`,
                toggleLabel: `${styles.activationToggle}`,
              }}
              currentStatus={isDealerActive ? "yes" : "no"}
              onSelect={(e) => handleEmpMembershipChange(dealerId, e)}
            />
          )}
          noRender={() => (
            <div
              data-testid={`disabledMembershipText_${dealerId}`}
              className={`${styles.switches} switch-3 switch-candy`}
            >
              {isDealerActive
                ? props.t("EmpMembership.active")
                : props.t("EmpMembership.inactive")}
            </div>
          )}
        />
      </Grid>
    </Grid>
  );
}

function EmpMembership(props) {
  return (
    <div>
      <div id="membershipHeader" className={`${styles.membershipTitle}`}>
        {props.t("EmpMembership.membershipHeader")}
      </div>
      <div>
        {/*"props.filteredDealers" has only Onboarded dealers.
        "props.dealerGroup" has all types of dealers */}
        {Object.keys(props.dealerGroup).length !== 0
          ? Object.keys(props.dealerGroup).map((commonId) => {
            return (
              <MembershipPanel
                key={`panelKey_${commonId}`}
                {...props}
                dealer={props.dealerGroup[commonId]}
              />
            );
          })
          : null}
      </div>
    </div>
  );
}

export default withTranslation("emp")(EmpMembership);
